// app/src/lib/plaid/walMerger.js — pulls encrypted WAL entries from S3 and merges
// them into Dexie. Idempotent: each WAL key is recorded in `appliedWalCheckpoints`
// and re-applying produces the same Dexie state.
//
// Concurrency: only one merge runs at a time (a single in-flight flag) — multiple
// tabs in the same browser profile may both kick off but Dexie's transactions and
// the checkpoint table keep them safe.

// SECURITY NOTE: WAL bodies are stored as plaintext JSON, protected at rest by
// S3 SSE and at access by per-user prefix isolation enforced server-side. See
// infra/lambdas/shared/walWriter.mjs for the rationale.

import { db } from '../db.js';
import { getAccessToken, currentClaims } from '../auth/cognitoClient.js';
import { getConfig } from '../config.js';
import { markBooksDirty } from '../backup/sharedSnapshot.js';

let inflight = false;

/**
 * Fetch the list of unmerged WAL keys, then decrypt + apply each in order.
 * @returns {Promise<{ applied: number, skipped: number }>}
 */
export async function mergePendingWal() {
  if (inflight) return { applied: 0, skipped: 0 };
  inflight = true;
  try {
    const claims = await currentClaims();
    if (!claims) return { applied: 0, skipped: 0 };
    const userId = claims.sub;
    const cfg = getConfig();

    const at = await getAccessToken();
    if (!at) return { applied: 0, skipped: 0 };

    // 1. List unmerged WAL object metadata (server returns presigned GETs).
    const listRes = await fetch(`${cfg.apiBaseUrl}/wal/list`, {
      headers: { authorization: `Bearer ${at}` },
    });
    if (!listRes.ok) throw new Error(`wal: list ${listRes.status}`);
    /** @type {{ key: string, url: string }[]} */
    const entries = (await listRes.json()).entries || [];

    let applied = 0;
    let skipped = 0;

    // Pull existing checkpoints in one shot for O(1) skip checks.
    const existing = new Set((await db.appliedWalCheckpoints.toArray()).map((c) => c.walKey));

    for (const { key, url } of entries) {
      if (existing.has(key)) { skipped++; continue; }
      const res = await fetch(url);
      if (!res.ok) throw new Error(`wal: GET ${res.status}`);
      /** @type {WalEntry} */
      const entry = await res.json();

      // Bind: the WAL entry must carry the same userId as the authenticated session.
      if (entry.userId !== userId) {
        // Don't checkpoint — surface a security alert.
        await db.alerts.add({
          alertId: cryptoUuid(), userId, severity: 'critical', kind: 'WAL_USER_MISMATCH',
          title: 'A sync entry was rejected for security reasons.', createdAt: Date.now(),
        });
        continue;
      }

      await applyWal(entry, userId);
      await db.appliedWalCheckpoints.put({ walKey: key, appliedAt: Date.now() });
      applied++;
    }
    // Cross-source dedup: an owner who started with CSV uploads and later
    // connected the bank via Plaid gets each overlapping transaction twice
    // (different id schemes, so nothing collapsed them). Drop the imported
    // copies the live Plaid feed now covers. Cheap + idempotent, so it also
    // cleans up dupes already on file the first time this runs post-connect.
    let deduped = 0;
    try { deduped = (await dedupeImportedAgainstPlaid(userId)).removed; }
    catch (e) { console.warn('[plaid] upload dedupe failed', e?.message || e); }

    // Team accounts: new Plaid data changed the books — schedule an owner
    // shared-snapshot upload so viewers see it (debounced; no-op for viewers).
    if (applied > 0 || deduped > 0) { try { markBooksDirty(); } catch { /* non-fatal */ } }
    return { applied, skipped, deduped };
  } finally {
    inflight = false;
  }
}

/**
 * @typedef {Object} WalEntry
 * @property {"transactions"|"item-alert"} kind
 * @property {string} userId
 * @property {string} plaidItemId
 * @property {number} writtenAt
 * @property {object[]=} added
 * @property {object[]=} modified
 * @property {string[]=} removedTxIds
 * @property {string=} alertType
 * @property {string=} institutionName
 */

async function applyWal(entry, userId) {
  await db.transaction('rw', db.plaidTransactions, db.plaidItems, db.plaidAccounts, db.alerts, async () => {
    if (entry.kind === 'transactions') {
      if (Array.isArray(entry.added) && entry.added.length) {
        await db.plaidTransactions.bulkPut(entry.added.map((t) => normalizeTx(t, userId, entry.plaidItemId)));
      }
      if (Array.isArray(entry.modified) && entry.modified.length) {
        await db.plaidTransactions.bulkPut(entry.modified.map((t) => normalizeTx(t, userId, entry.plaidItemId)));
      }
      if (Array.isArray(entry.removedTxIds) && entry.removedTxIds.length) {
        await db.plaidTransactions.bulkDelete(entry.removedTxIds);
      }
      const item = await db.plaidItems.get(entry.plaidItemId);
      if (item) await db.plaidItems.put({ ...item, lastSyncedAt: entry.writtenAt });
    } else if (entry.kind === 'balances') {
      // Refresh the stored account balances (Plaid doesn't push these with the
      // transaction feed; the exchange + webhook fetch them and write this entry
      // on connect and every sync). Keeps the cash position + reserve anchored
      // to the live balance instead of a stale import snapshot.
      const asOf = entry.writtenAt ? isoDate(entry.writtenAt) : undefined;
      for (const b of (entry.balances || [])) {
        const acct = await db.plaidAccounts.get(b.plaidAccountId);
        if (!acct) continue; // account row created by linkClient at connect
        await db.plaidAccounts.put({
          ...acct,
          currentBalanceCents: b.currentBalanceCents,
          availableBalanceCents: b.availableBalanceCents,
          ...(b.limitCents != null ? { limitCents: b.limitCents } : {}),
          currency: acct.currency || b.isoCurrencyCode || 'USD',
          ...(asOf ? { balanceAsOf: asOf } : {}),
        });
      }
    } else if (entry.kind === 'item-alert') {
      const ALLOWED_STATUSES = new Set(['HEALTHY', 'ITEM_LOGIN_REQUIRED', 'PENDING_EXPIRATION', 'REVOKED']);
      const safeStatus = ALLOWED_STATUSES.has(entry.alertType) ? entry.alertType : 'ITEM_LOGIN_REQUIRED';
      const item = await db.plaidItems.get(entry.plaidItemId);
      if (item) await db.plaidItems.put({ ...item, status: safeStatus });
      if (entry.alertType !== 'HEALTHY') {
        // §12 alert-catalog mapping for Plaid item statuses:
        //   ITEM_LOGIN_REQUIRED  → danger   (critical in our internal vocab)
        //   PENDING_EXPIRATION   → warning
        //   USER_PERMISSION_REVOKED / REVOKED → warning
        const severity =
          safeStatus === 'ITEM_LOGIN_REQUIRED' ? 'critical' :
          safeStatus === 'PENDING_EXPIRATION'  ? 'warning'  :
          'warning';
        await db.alerts.add({
          alertId: cryptoUuid(),
          userId,
          severity,
          kind: `PLAID_${safeStatus}`,
          title: `${entry.institutionName || 'A bank'} needs attention`,
          createdAt: entry.writtenAt,
          dismissed: 0,
          meta: { plaidItemId: entry.plaidItemId, institutionName: entry.institutionName },
        });
      }
    }
  });
}

function normalizeTx(t, userId, plaidItemId) {
  return {
    plaidTxId: t.plaidTxId,
    userId,
    plaidItemId,
    plaidAccountId: t.plaidAccountId,
    date: t.date,
    amountCents: Math.round(t.amount * 100), // Plaid: positive = outflow.
    pending: !!t.pending,
    merchantName: t.merchantName || t.name || '',
    category: t.category,
  };
}

function cryptoUuid() {
  return crypto.randomUUID();
}

// Server timestamp (ms) → local 'YYYY-MM-DD', used to stamp a balance's as-of.
function isoDate(ms) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Whole-day difference between two 'YYYY-MM-DD' strings (a − b), or Infinity if
// either can't be parsed.
function dayDiff(a, b) {
  const da = Date.parse(a + 'T00:00:00Z'), db2 = Date.parse(b + 'T00:00:00Z');
  if (Number.isNaN(da) || Number.isNaN(db2)) return Infinity;
  return Math.round((da - db2) / 86400000);
}

/**
 * Pure 1:1 matcher between CSV-imported rows and live Plaid rows. A pair is the
 * "same transaction" when the signed amount is identical (both stores use the
 * Plaid convention: positive = outflow) and the dates are within `windowDays`
 * (bank posted-date vs Plaid authorized-date can drift a day or two). Exact-date
 * matches are claimed first so a windowed match can't steal one. Each imported
 * row and each Plaid row is used at most once, so genuinely distinct same-amount
 * transactions aren't collapsed.
 *
 * @returns {{ deleteIds: string[], balanceTransfers: Array<{plaidTxId:string, balanceCents:number, balanceAsOf?:string}> }}
 */
export function pairImportedDuplicates(imported, plaid, windowDays = 3) {
  const byAmount = new Map();
  for (const r of imported) {
    if (!byAmount.has(r.amountCents)) byAmount.set(r.amountCents, []);
    byAmount.get(r.amountCents).push({ row: r, matched: false });
  }
  const deleteIds = [];
  const balanceTransfers = [];
  const usedP = new Set();
  const pass = (requireExact) => {
    for (let pi = 0; pi < plaid.length; pi++) {
      if (usedP.has(pi)) continue;
      const p = plaid[pi];
      const cands = byAmount.get(p.amountCents);
      if (!cands) continue;
      let best = null, bestDiff = Infinity;
      for (const c of cands) {
        if (c.matched) continue;
        const diff = Math.abs(dayDiff(c.row.date, p.date));
        const ok = requireExact ? diff === 0 : diff <= windowDays;
        if (ok && diff < bestDiff) { best = c; bestDiff = diff; }
      }
      if (best) {
        best.matched = true;
        usedP.add(pi);
        deleteIds.push(best.row.plaidTxId);
        // Preserve the per-row running balance the CSV carried (Plaid rows don't
        // have one) — some reports read the exact bank balance as of a date.
        if (best.row.balanceCents != null && p.balanceCents == null) {
          balanceTransfers.push({
            plaidTxId: p.plaidTxId, balanceCents: best.row.balanceCents,
            ...(best.row.balanceAsOf != null ? { balanceAsOf: best.row.balanceAsOf } : {}),
          });
        }
      }
    }
  };
  pass(true);   // exact-date pairs first
  pass(false);  // then within the drift window
  return { deleteIds, balanceTransfers };
}

/**
 * Remove CSV/upload transactions that the live Plaid feed now covers. Imported
 * rows carry source 'upload'|'csv'; live Plaid rows have no source. Only the
 * overlapping date span is scanned. Idempotent.
 */
export async function dedupeImportedAgainstPlaid(userId, windowDays = 3) {
  const imported = await db.plaidTransactions.where('userId').equals(userId)
    .filter((t) => t.source === 'upload' || t.source === 'csv').toArray();
  if (!imported.length) return { removed: 0 };

  const lo = imported.reduce((m, t) => (t.date < m ? t.date : m), imported[0].date);
  const hi = imported.reduce((m, t) => (t.date > m ? t.date : m), imported[0].date);
  const inRange = await db.plaidTransactions
    .where('[userId+date]').between([userId, lo], [userId, hi], true, true).toArray();
  const plaid = inRange.filter((t) => t.source == null); // live Plaid rows only
  if (!plaid.length) return { removed: 0 };

  const { deleteIds, balanceTransfers } = pairImportedDuplicates(imported, plaid, windowDays);
  if (!deleteIds.length && !balanceTransfers.length) return { removed: 0 };

  await db.transaction('rw', db.plaidTransactions, async () => {
    for (const b of balanceTransfers) {
      const row = await db.plaidTransactions.get(b.plaidTxId);
      if (row) await db.plaidTransactions.put({ ...row, balanceCents: b.balanceCents, ...(b.balanceAsOf != null ? { balanceAsOf: b.balanceAsOf } : {}) });
    }
    if (deleteIds.length) await db.plaidTransactions.bulkDelete(deleteIds);
  });
  return { removed: deleteIds.length };
}
